/**
 * Printable evidence pack (MERGE-PLAN §4.4). Rendered into the app DOM but hidden on
 * screen; the `@media print` stylesheet reveals `.print-target` and hides the rest,
 * so the browser's Print → Save as PDF produces the auditor evidence pack with no
 * new dependency and no network (zero-egress, CSP-clean).
 */
import { FRAMEWORK_LABELS, type FrameworkId } from '@/compliance/types';
import type { EvidencePack } from '@/compliance/evidence/types';

const FRAMEWORK_ORDER: readonly FrameworkId[] = ['cis', 'nist', 'iso', 'soc2', 'hipaa', 'secScore'];

export function EvidencePrintReport({ pack }: { pack: EvidencePack }) {
  return (
    <section className="panel print-target print-only evidence-print">
      <header className="evidence-print__head">
        <h1>Compliance Audit Evidence Pack</h1>
        <p className="evidence-print__meta">
          <strong>{pack.meta.tenantName}</strong>
          {pack.meta.tenantId ? ` · Tenant ${pack.meta.tenantId}` : ''}
          <br />
          Collected by {pack.meta.collectedBy} · {new Date(pack.meta.collectedAt).toLocaleString()}
          <br />
          Posture score {pack.posture.score}/100 (Grade {pack.posture.grade}) · {pack.meta.controlsTested} controls
          tested · {pack.meta.controlsPassed} passing · {pack.meta.controlsFailed} failing
        </p>
      </header>

      <h2>Framework coverage</h2>
      <table className="matrix">
        <thead>
          <tr><th>Framework</th><th>Tested</th><th>Passed</th><th>Failed</th><th>Coverage</th></tr>
        </thead>
        <tbody>
          {pack.coverage
            .filter((c) => c.total > 0)
            .map((c) => (
              <tr key={c.framework}>
                <td>{FRAMEWORK_LABELS[c.framework]}</td>
                <td>{c.total}</td>
                <td>{c.passed}</td>
                <td>{c.failed}</td>
                <td>{c.score}%</td>
              </tr>
            ))}
        </tbody>
      </table>

      <h2>Control evidence</h2>
      {pack.records.map((r) => (
        <div key={r.controlId} className="evidence-print__control">
          <h3>
            [{r.status.toUpperCase()}] {r.title}
            <span className="evidence-print__sev"> · {r.severity}</span>
          </h3>
          <p className="evidence-print__obj">{r.objective}</p>

          <p className="evidence-print__row">
            <strong>Provenance:</strong> {r.provenance.endpoints.join(', ')} ·{' '}
            {new Date(r.provenance.collectedAt).toISOString()}
          </p>

          <p className="evidence-print__row">
            <strong>Mappings:</strong>{' '}
            {FRAMEWORK_ORDER.filter((f) => r.frameworks[f].length > 0)
              .map((f) => `${FRAMEWORK_LABELS[f]}: ${r.frameworks[f].join(', ')}`)
              .join('  |  ')}
          </p>

          {r.affected.length > 0 && (
            <>
              <p className="evidence-print__row"><strong>Affected objects ({r.affected.length}):</strong></p>
              <ul className="evidence-print__objects">
                {r.affected.slice(0, 50).map((a) => (
                  <li key={a.id}>
                    {a.label}
                    {a.detail ? ` — ${a.detail}` : ''} <em>({a.type})</em>
                  </li>
                ))}
                {r.affected.length > 50 && <li>… and {r.affected.length - 50} more</li>}
              </ul>
            </>
          )}

          {r.status === 'fail' && (
            <p className="evidence-print__row"><strong>Remediation:</strong> {r.remediation}</p>
          )}
        </div>
      ))}

      <footer className="evidence-print__foot">
        Generated client-side by Health365 · zero-egress. Framework citations are a starting point; the auditor
        validates final applicability. Compliance is a floor, not proof of security.
      </footer>
    </section>
  );
}
